import React, { useState, useEffect } from "react";
import { Box, Typography, Select, MenuItem, InputLabel, Paper, Grid, Card, CardContent } from "@mui/material";
import DashboardCard from "../components/DashboardCard";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { api, normalizeCountry } from "common";
import { FirebaseConfig as firebaseConfig } from "../config/FirebaseConfig";
import { colors } from "components/Theme/WebTheme";

const Dashboard = () => {
  const [mylocation, setMylocation] = useState(null);
  // Where to centre the map when the browser gives no position (location off,
  // denied, or never answering): the first driver on the map. Set once, so
  // the map does not jump as that driver moves.
  const [fallbackCenter, setFallbackCenter] = useState(null);
  const [locations, setLocations] = useState([]);
  const [dailygross, setDailygross] = useState(0);
  const [monthlygross, setMonthlygross] = useState(0);
  const [totalgross, setTotalgross] = useState(0);

  const [settings, setSettings] = useState({});
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const usersdata = useSelector((state) => state.usersdata);
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const settingsdata = useSelector((state) => state.settingsdata);
  const auth = useSelector((state) => state.auth);
  const cars = useSelector((state) => state.cartypes.cars);
  const [allCompleteCount, setAllCompleteCount] = useState([]);
  const [allCancelCount, setAllCancelCount] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [driverCount, setDriverCount] = useState(0);
  const [customerCount, setCustomerCount] = useState(0);
  const dispatch = useDispatch();
  const [country, setCountry] = useState("all");
  const [symbol, setSymbol] = useState("");
  const { fetchDrivers, clearFetchDrivers } = api;
  const [exchangeRates, setExchangeRates] = useState({});

  useEffect(() => {
    dispatch(fetchDrivers("web"));
    return () => {
      dispatch(clearFetchDrivers());
    };
  }, [dispatch, fetchDrivers, clearFetchDrivers]);

  useEffect(() => {
    if (mylocation == null) {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          setMylocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }),
        (error) => console.log(error)
      );
    }
  }, [mylocation]);

  useEffect(() => {
    if (settingsdata.settings) {
      setSettings(settingsdata.settings);

      if (country === "all") {
        setSymbol(settingsdata.settings.symbol);
      } else if (country === "burundi") {
        setSymbol(settingsdata.settings.symbol_burundi);
      } else if (country === "canada") {
        setSymbol(settingsdata.settings.symbol_canada);
      } else if (country === "democratic republic of the congo") {
        setSymbol(settingsdata.settings.symbol_drc);
      } else if (country === "kenya") {
        setSymbol(settingsdata.settings.symbol_kenya);
      } else if (country === "nigeria") {
        setSymbol(settingsdata.settings.symbol_nigeria);
      } else if (country === "south africa") {
        setSymbol(settingsdata.settings.symbol_rsa);
      } else if (country === "rwanda") {
        setSymbol(settingsdata.settings.symbol_rwanda);
      } else if (country === "zambia") {
        setSymbol(settingsdata.settings.symbol_zambia);
      } else if (country === "uganda") {
        setSymbol(settingsdata.settings.symbol_uganda);
      } else if (country === "united republic of tanzania") {
        setSymbol(settingsdata.settings.symbol_tanzania);
      } else if (country === "benin") {
        setSymbol(settingsdata.settings.symbol_benin);
      } else if (country === "burkina_faso") {
        setSymbol(settingsdata.settings.symbol_burkina_faso);
      } else if (country === "cameroon") {
        setSymbol(settingsdata.settings.symbol_cameroon);
      } else if (country === "gabon") {
        setSymbol(settingsdata.settings.symbol_gabon);
      } else if (country === "ghana") {
        setSymbol(settingsdata.settings.symbol_ghana);
      } else if (country === "lesotho") {
        setSymbol(settingsdata.settings.symbol_lesotho);
      } else if (country === "malawi") {
        setSymbol(settingsdata.settings.symbol_malawi);
      } else if (country === "mozambique") {
        setSymbol(settingsdata.settings.symbol_mozambique);
      } else if (country === "senegal") {
        setSymbol(settingsdata.settings.symbol_senegal);
      } else if (country === "sierra_leone") {
        setSymbol(settingsdata.settings.symbol_sierra_leone);
      } else {
        setSymbol(settingsdata.settings.symbol);
      }
    }
  }, [settingsdata.settings, country]);

  useEffect(() => {
    if (usersdata.drivers && bookinglistdata.bookings) {
      const liveBookings = bookinglistdata.bookings.filter(
        (bkg) => bkg.status === "STARTED"
      );
      const drivers = usersdata.drivers;
      let locs = [];
      for (let i = 0; i < drivers.length; i++) {
        if (drivers[i].location) {
          let carImage =
            "https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png";
          let bookingRef = null;
          for (let j = 0; j < cars.length; j++) {
            if (cars[j].name === drivers[i].carType) {
              carImage = cars[j].image;
            }
          }
          for (let j = 0; j < liveBookings.length; j++) {
            if (liveBookings[j].driver === drivers[i].uid) {
              bookingRef = liveBookings[j].reference;
            }
          }
          locs.push({
            id: i,
            lat: drivers[i].location.lat,
            lng: drivers[i].location.lng,
            drivername: drivers[i].firstName + " " + drivers[i].lastName,
            carnumber: drivers[i].vehicleNumber,
            cartype: drivers[i].carType,
            carImage: carImage,
            bookingRef: bookingRef,
          });
        }
      }
      setLocations(locs);
      if (locs.length > 0) {
        setFallbackCenter((c) => c || { lat: locs[0].lat, lng: locs[0].lng });
      }
    }
  }, [
    usersdata.drivers,
    auth.profile.usertype,
    auth.profile.uid,
    cars,
    bookinglistdata.bookings,
    settings,
  ]);

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        // Create an array of currency codes we need rates for
        const currencies = [
          "BIF", // Burundi Franc
          "CAD", // Canadian Dollar
          "CDF", // Congolese Franc
          "KES", // Kenyan Shilling
          "NGN", // Nigerian Naira
          "ZAR", // South African Rand
          "RWF", // Rwandan Franc
          "ZMW", // Zambian Kwacha
          "UGX", // Ugandan Shilling
          "TZS", // Tanzanian Shilling
          "BGN", // Bulgarian Lev
          "RON", // Romanian Leu
          "ALL", // Albanian Lek
          "MZN", // Mozambican Metical
          "XOF", // West African CFA Franc 
          "LSL", // Lesotho Loti
          "MWK", // Malawi Kwacha
          "MUR", // Mauritian Rupee
          "MZN", // Mozambican Metical
          "XOF", // West African CFA Franc
          
        ];
        
        // Rates come from our own usd_rates function, which holds the FX API
        // key server-side and only answers signed-in admins.
        const token = await api.getIdToken();
        const response = await fetch(
          `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net/usd_rates`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ currencies }),
          }
        );
        const { rates = {} } = await response.json();
        setExchangeRates(rates);
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
      }
    };
    
    fetchExchangeRates();
    // Fetch rates every hour
    const interval = setInterval(fetchExchangeRates, 3600000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (bookinglistdata.bookings) {
      let today = new Date();
      let monthlyTotal = 0;
      let yearlyTotal = 0;
      let todayTotal = 0;
      
      for (let i = 0; i < bookinglistdata.bookings.length; i++) {
        const booking = bookinglistdata.bookings[i];
        
        if (
          (booking.status === "PAID" || booking.status === "COMPLETE") &&
          ((auth.profile.usertype === "fleetadmin" &&
            booking.country &&
            normalizeCountry(booking.country) === normalizeCountry(auth.profile.country)) ||
            auth.profile.usertype === "admin")
        ) {
          const { tripdate, convenience_fees, admin_fees, fleetCommission, discount, country: bookingCountry } = booking;
          let tDate = new Date(tripdate);
          
          // Get conversion rate based on booking's country
          let conversionRate = 1;
          if (country === "all" && bookingCountry && exchangeRates) {
            // keyed by normalizeCountry's canonical names, so every stored
            // spelling ("tanzania", "United Republic of Tanzania", ...) converts
            const currencyMap = {
              "burundi": "BIF",
              "canada": "CAD",
              "democratic republic of the congo": "CDF",
              "kenya": "KES",
              "nigeria": "NGN",
              "south africa": "ZAR",
              "rwanda": "RWF",
              "zambia": "ZMW",
              "uganda": "UGX",
              "tanzania": "TZS",
              "benin": "XOF",
              "burkina faso": "XOF",
              "cameroon": "XAF",
              "gabon": "XAF",
              "ghana": "GHS", 
              "lesotho": "LSL",
              "malawi": "MWK",
              "mozambique": "MZN",
              "senegal": "XOF",
              "sierra leone": "SLL"
            };
            
            const currencyCode = currencyMap[normalizeCountry(bookingCountry)];
            if (currencyCode && exchangeRates[currencyCode]) {
              conversionRate = exchangeRates[currencyCode];
            }
          }
          
          // Only process if it matches country filter
          const isMatchingCountry = country === "all" || 
            (booking.country && normalizeCountry(booking.country) === normalizeCountry(country));

          // The platform's cut. convenience_fees is deliberately 0 for now
          // (FareCalculator: "not charging for now"); what the platform keeps
          // is admin_fees, taken from each Tanzania fare. Counting only
          // convenience_fees left every earnings card at 0.
          const platformFees =
            (parseFloat(convenience_fees) || 0) + (parseFloat(admin_fees) || 0);

          if (
            isMatchingCountry &&
            platformFees > 0 &&
            auth.profile.usertype === "admin"
          ) {
            const amount = platformFees - (parseFloat(discount) || 0);
            const convertedFees = country === "all" ? amount * conversionRate : amount;
            
            if (
              tDate.getDate() === today.getDate() &&
              tDate.getMonth() === today.getMonth() &&
              tDate.getFullYear() === today.getFullYear()
            ) {
              todayTotal += convertedFees;
            }
            if (
              tDate.getMonth() === today.getMonth() &&
              tDate.getFullYear() === today.getFullYear()
            ) {
              monthlyTotal += convertedFees;
            }
            yearlyTotal += convertedFees;
          }
          
          if (
            isMatchingCountry &&
            fleetCommission &&
            parseFloat(fleetCommission) > 0 &&
            auth.profile.usertype === "fleetadmin"
          ) {
            const amount = parseFloat(fleetCommission);
            const convertedCommission = country === "all" ? amount * conversionRate : amount;
            
            if (
              tDate.getDate() === today.getDate() &&
              tDate.getMonth() === today.getMonth() &&
              tDate.getFullYear() === today.getFullYear()
            ) {
              todayTotal += convertedCommission;
            }
            if (
              tDate.getMonth() === today.getMonth() &&
              tDate.getFullYear() === today.getFullYear()
            ) {
              monthlyTotal += convertedCommission;
            }
            yearlyTotal += convertedCommission;
          }
        }
      }
      
      setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
      setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
      setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
    }
  }, [
    bookinglistdata.bookings,
    settings,
    auth.profile.uid,
    auth.profile.usertype,
    auth.profile.country,
    country,
    exchangeRates
  ]);

  const barChartOptions = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    colors: [colors.GREEN, colors.RED],
    options: {
      maintainAspectRatio: false,
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
      chart: {
        height: 350,
        type: "area",
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
      },
    },
  };

  const barChart = [
    {
      name: t("completed_bookings"),
      data: allCompleteCount,
    },
    {
      name: t("cancelled_bookings"),
      data: allCancelCount,
    },
  ];

  useEffect(() => {
    if (auth?.profile && auth?.profile?.country) {
      setCountry(auth?.profile?.country.toLowerCase());
    }
  }, [auth?.profile]);

  useEffect(() => {
    if (usersdata?.users?.length) {
      // Map country names to keys
      const countryMap = {
        "democratic republic of the congo": "democratic republic of the congo",
        "south africa": "south africa",
        burundi: "burundi",
        canada: "canada",
        kenya: "kenya",
        nigeria: "nigeria",
        rwanda: "rwanda",
        zambia: "zambia",
        uganda: "uganda",
        "united republic of tanzania": "tanzania",
        benin: "benin",
        "burkina_faso": "burkina_faso",
        cameroon: "cameroon",
        gabon: "gabon",
        ghana: "ghana",
        lesotho: "lesotho",
        malawi: "malawi",
        mozambique: "mozambique",
        senegal: "senegal",
        "sierra_leone": "sierra_leone"
      };

      const countryData = {
        all: { driverCount: 0, activeCount: 0, customerCount: 0 },
        "democratic republic of the congo": { driverCount: 0, activeCount: 0, customerCount: 0 },
        "south africa": { driverCount: 0, activeCount: 0, customerCount: 0 },
        burundi: { driverCount: 0, activeCount: 0, customerCount: 0 },
        canada: { driverCount: 0, activeCount: 0, customerCount: 0 },
        kenya: { driverCount: 0, activeCount: 0, customerCount: 0 },
        nigeria: { driverCount: 0, activeCount: 0, customerCount: 0 },
        rwanda: { driverCount: 0, activeCount: 0, customerCount: 0 },
        zambia: { driverCount: 0, activeCount: 0, customerCount: 0 },
        uganda: { driverCount: 0, activeCount: 0, customerCount: 0 },
        tanzania: { driverCount: 0, activeCount: 0, customerCount: 0 },
        benin: { driverCount: 0, activeCount: 0, customerCount: 0 },
        "burkina_faso": { driverCount: 0, activeCount: 0, customerCount: 0 },
        cameroon: { driverCount: 0, activeCount: 0, customerCount: 0 },
        gabon: { driverCount: 0, activeCount: 0, customerCount: 0 },
        ghana: { driverCount: 0, activeCount: 0, customerCount: 0 },
        lesotho: { driverCount: 0, activeCount: 0, customerCount: 0 },
        malawi: { driverCount: 0, activeCount: 0, customerCount: 0 },
        mozambique: { driverCount: 0, activeCount: 0, customerCount: 0 },
        senegal: { driverCount: 0, activeCount: 0, customerCount: 0 },
        "sierra_leone": { driverCount: 0, activeCount: 0, customerCount: 0 }
      };

      const isAdminOrFleetAdmin = ["fleetadmin", "admin"].includes(
        auth?.profile?.usertype
      );

      if (isAdminOrFleetAdmin) {
        usersdata.users.forEach((user) => {
          const { usertype, country, driverActiveStatus } = user;

          // Check if country is valid before mapping
          const countryKey = country && countryMap[country.toLowerCase()];

          // General counters
          if (usertype === "driver") countryData.all.driverCount++;
          if (usertype === "customer") countryData.all.customerCount++;
          if (driverActiveStatus) countryData.all.activeCount++;

          // Country-specific counters
          if (countryKey && countryData[countryKey]) {
            if (usertype === "driver") countryData[countryKey].driverCount++;
            if (usertype === "customer")
              countryData[countryKey].customerCount++;
            if (driverActiveStatus) countryData[countryKey].activeCount++;
          }
        });
      }

      // Update state based on the selected country
      const selectedCountry = country?.toLowerCase() || "all";
      if (selectedCountry === "all" || !countryMap[selectedCountry]) {
        setDriverCount(countryData.all.driverCount);
        setActiveCount(countryData.all.activeCount);
        setCustomerCount(countryData.all.customerCount);
      } else {
        const key = countryMap[selectedCountry];
        setDriverCount(countryData[key]?.driverCount || 0);
        setActiveCount(countryData[key]?.activeCount || 0);
        setCustomerCount(countryData[key]?.customerCount || 0);
      }
    } else {
      // Reset counts if no user data is present
      setDriverCount(0);
      setActiveCount(0);
      setCustomerCount(0);
    }
  }, [usersdata?.users, auth?.profile, country]);

  useEffect(() => {
    let allCompleteCount = [];
    let allCancelCount = [];
    let months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    if (bookinglistdata.bookings) {
      for (let i = 0; i < months.length; i++) {
        let completeCount = 0;
        let cancelCount = 0;
        for (let j = 0; j < bookinglistdata.bookings.length; j++) {
          const { tripdate } = bookinglistdata.bookings[j];
          let tDate = new Date(tripdate);
          if (
            months[i] === months[tDate.getMonth()] &&
            (bookinglistdata.bookings[j].status === "PAID" ||
              bookinglistdata.bookings[j].status === "COMPLETE")
          ) {
            completeCount = completeCount + 1;
          }
          if (
            months[i] === months[tDate.getMonth()] &&
            bookinglistdata.bookings[j].status === "CANCELLED"
          ) {
            cancelCount = cancelCount + 1;
          }
        }
        allCompleteCount.push(completeCount);
        allCancelCount.push(cancelCount);
      }
    }
    setAllCompleteCount(allCompleteCount);
    setAllCancelCount(allCancelCount);
  }, [bookinglistdata.bookings]);

  const handleChange = (event) => {
    setCountry(event.target.value);
  };

  return bookinglistdata.loading || usersdata.loading ? (
    <CircularLoading />
  ) : (
    <Box sx={{ p: 3 }}>
      {auth?.profile?.usertype !== "fleetadmin" && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <InputLabel id="demo-simple-select-label" sx={{ mb: 1 }}>
            Select Country
          </InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            fullWidth
            value={country}
            label="Select Country"
            onChange={handleChange}
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="burundi">Burundi</MenuItem>
            <MenuItem value="canada">Canada</MenuItem>
            <MenuItem value="democratic republic of the congo">DRC</MenuItem>
            <MenuItem value="kenya">Kenya</MenuItem>
            <MenuItem value="nigeria">Nigeria</MenuItem>
            <MenuItem value="south africa">South Africa</MenuItem>
            <MenuItem value="rwanda">Rwanda</MenuItem>
            <MenuItem value="zambia">Zambia</MenuItem>
            <MenuItem value="uganda">Uganda</MenuItem>
            <MenuItem value="united republic of tanzania">United Republic of Tanzania</MenuItem>
            <MenuItem value="benin">Benin</MenuItem>
            <MenuItem value="burkina_faso">Burkina Faso</MenuItem>
            <MenuItem value="cameroon">Cameroon</MenuItem>
            <MenuItem value="gabon">Gabon</MenuItem>
            <MenuItem value="ghana">Ghana</MenuItem>
            <MenuItem value="lesotho">Lesotho</MenuItem>
            <MenuItem value="malawi">Malawi</MenuItem>
            <MenuItem value="mozambique">Mozambique</MenuItem>
            <MenuItem value="senegal">Senegal</MenuItem>
            <MenuItem value="sierra_leone">Sierra Leone</MenuItem>
          </Select>
        </Paper>
      )}

      <Typography
        variant="h4"
        sx={{
          mb: 3,
          fontWeight: 600,
          color: colors.GREEN,
          textAlign: isRTL === "rtl" ? "right" : "left",
        }}
      >
        {t("gross_earning")}
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <DashboardCard
            crdStyle={{
              display: "flex",
              borderRadius: "16px",
              backgroundColor: "#CC3372",
              color: colors.WHITE,
              boxShadow: "0 4px 20px rgba(204, 51, 114, 0.15)",
              transition: "transform 0.2s",
              "&:hover": {
                transform: "translateY(-5px)",
              },
            }}
            title={t("today_text")}
            image={require("../assets/img/money1.jpg").default}
          >
            {settings.swipe_symbol === false ? symbol + " " + dailygross : dailygross + " " + symbol}
          </DashboardCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <DashboardCard
            crdStyle={{
              display: "flex",
              borderRadius: "16px",
              backgroundColor: "#A755C2",
              color: colors.WHITE,
              boxShadow: "0 4px 20px rgba(167, 85, 194, 0.15)",
              transition: "transform 0.2s",
              "&:hover": {
                transform: "translateY(-5px)",
              },
            }}
            title={t("this_month_text")}
            image={require("../assets/img/money2.jpg").default}
          >
            {settings.swipe_symbol === false ? symbol + " " + monthlygross : monthlygross + " " + symbol}
          </DashboardCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <DashboardCard
            crdStyle={{
              display: "flex",
              borderRadius: "16px",
              backgroundColor: "#2CDE3A",
              color: colors.WHITE,
              boxShadow: "0 4px 20px rgba(44, 222, 58, 0.15)",
              transition: "transform 0.2s",
              "&:hover": {
                transform: "translateY(-5px)",
              },
            }}
            title={t("total")}
            image={require("../assets/img/money3.jpg").default}
          >
            {settings.swipe_symbol === false ? symbol + " " + totalgross : totalgross + " " + symbol}
          </DashboardCard>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              height: "100%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: colors.GREEN,
                textAlign: isRTL === "rtl" ? "right" : "left",
              }}
            >
              {t("real_time_driver_section_text")}
            </Typography>
            {(mylocation && mylocation.lat) || fallbackCenter ? (
              <GoogleMap
                zoom={10}
                center={mylocation && mylocation.lat ? mylocation : fallbackCenter}
                mapContainerStyle={{ height: "380px", borderRadius: "12px" }}
              >
                {locations.map((marker) => (
                  <Marker
                    position={{ lat: marker.lat, lng: marker.lng }}
                    key={marker.id}
                    icon={{
                      url: marker.carImage,
                      scaledSize: new window.google.maps.Size(35, 25),
                    }}
                  >
                    <InfoWindow
                      position={{ lat: marker.lat, lng: marker.lng }}
                      options={{
                        pixelOffset: new window.google.maps.Size(0, -32),
                      }}
                    >
                      <div>
                        {marker.drivername}
                        <br />
                        {marker.carnumber}
                      </div>
                    </InfoWindow>
                  </Marker>
                ))}
              </GoogleMap>
            ) : null}
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  backgroundColor: "#CC3372",
                  boxShadow: "0 4px 20px rgba(204, 51, 114, 0.15)",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    sx={{
                      color: "white",
                      textAlign: "center",
                      fontSize: 18,
                      mb: 1,
                    }}
                  >
                    {t("total_cumtomer")}
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      textAlign: "center",
                      fontSize: 32,
                      fontWeight: "bold",
                    }}
                  >
                    {customerCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  backgroundColor: "#A755C2",
                  boxShadow: "0 4px 20px rgba(167, 85, 194, 0.15)",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    sx={{
                      color: "white",
                      textAlign: "center",
                      fontSize: 18,
                      mb: 1,
                    }}
                  >
                    {t("total_drivers")}
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      textAlign: "center",
                      fontSize: 32,
                      fontWeight: "bold",
                    }}
                  >
                    {driverCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 2,
                  backgroundColor: "#2CDE3A",
                  boxShadow: "0 4px 20px rgba(44, 222, 58, 0.15)",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    sx={{
                      color: "white",
                      textAlign: "center",
                      fontSize: 18,
                      mb: 1,
                    }}
                  >
                    {t("active_driver")}
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      textAlign: "center",
                      fontSize: 32,
                      fontWeight: "bold",
                    }}
                  >
                    {activeCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mt: 4,
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Typography
          sx={{
            mb: 3,
            color: colors.GREEN,
            fontWeight: 600,
            fontSize: "1.5rem",
          }}
        >
          {t("booking_chart")}
        </Typography>
        <Chart
          options={barChartOptions}
          series={barChart}
          type="area"
          width="100%"
          height={360}
        />
      </Paper>
    </Box>
  );
};

export default Dashboard;
